/**
 * PCFDetailPanel — Panel derecho (drill-down) para conciliacion UNE-Credito → FSA
 * Muestra la lista paginada de transferencias/abonos al hacer click en una celda.
 */

import { useState, useMemo } from 'react';
import { X, Download, Eye } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Pagination } from '@/components/ui/Pagination';
import {
  type PCFDrillDownSelection,
  PCF_SUMMARY_COLUMNS,
  PCF_RECONCILIATION_CONFIG,
  MONTH_NAMES,
} from '@/types/pcf-reconciliation.types';
import { CollectorDetailModal } from './CollectorDetailModal';

const PAGE_SIZE = 100;

interface Props {
  selection: PCFDrillDownSelection;
  items: Record<string, unknown>[];
  onClose: () => void;
}

// ─── Color maps ─────────────────────────────────────────────────────────────

const HEADER_COLOR_MAP: Record<string, { text: string; border: string; bg: string }> = {
  green: { text: 'text-green-400', border: 'border-green-500/30', bg: 'bg-green-500/5' },
  red: { text: 'text-red-400', border: 'border-red-500/30', bg: 'bg-red-500/5' },
  amber: { text: 'text-amber-400', border: 'border-amber-500/30', bg: 'bg-amber-500/5' },
  rose: { text: 'text-rose-400', border: 'border-rose-500/30', bg: 'bg-rose-500/5' },
  slate: { text: 'text-slate-400', border: 'border-slate-500/30', bg: 'bg-slate-500/5' },
};

const STATUS_BADGE_COLORS: Record<string, string> = {
  CONFIRMED: 'bg-green-500/20 text-green-400',
  SENT: 'bg-blue-500/20 text-blue-400',
  PENDING: 'bg-amber-500/20 text-amber-400',
  CREATED: 'bg-slate-500/20 text-slate-400',
};

// ─── Column config per status ───────────────────────────────────────────────

interface DetailColumn {
  key: string;
  label: string;
  format?: 'date' | 'currency' | 'status' | 'mono';
}

function getDetailColumns(statusKey: string): DetailColumn[] {
  if (statusKey === 'ok') {
    return [
      { key: 'transferDate', label: 'Fecha', format: 'date' },
      { key: 'totalAmountMxn', label: 'Monto MXN', format: 'currency' },
      { key: 'salidaCuenta', label: 'Salida' },
      { key: 'entradaCuenta', label: 'Entrada' },
      { key: 'salidaReferencia', label: 'Ref. bancaria' },
      { key: 'transferStatus', label: 'Status', format: 'status' },
    ];
  }
  if (statusKey === 'salioSinEntrar') {
    return [
      { key: 'transferId', label: 'Funding ID', format: 'mono' },
      { key: 'transferDate', label: 'Fecha', format: 'date' },
      { key: 'totalAmountMxn', label: 'Monto MXN', format: 'currency' },
      { key: 'salidaCuenta', label: 'Salida (cuenta)' },
      { key: 'salidaReferencia', label: 'Ref. salida' },
    ];
  }
  if (statusKey === 'entroSinSalir') {
    return [
      { key: 'transferId', label: 'Funding ID', format: 'mono' },
      { key: 'transferDate', label: 'Fecha', format: 'date' },
      { key: 'totalAmountMxn', label: 'Monto MXN', format: 'currency' },
      { key: 'entradaCuenta', label: 'Entrada (cuenta)' },
      { key: 'entradaReferencia', label: 'Ref. entrada' },
    ];
  }
  if (statusKey === 'sinCuadrar') {
    return [
      { key: 'transferId', label: 'Funding ID', format: 'mono' },
      { key: 'transferDate', label: 'Fecha', format: 'date' },
      { key: 'totalAmountMxn', label: 'Monto MXN', format: 'currency' },
      { key: 'sinCuadrarMotivo', label: 'Motivo' },
    ];
  }
  // sinBanco
  return [
    { key: 'transferId', label: 'Funding ID', format: 'mono' },
    { key: 'transferDate', label: 'Fecha', format: 'date' },
    { key: 'totalAmountMxn', label: 'Monto MXN', format: 'currency' },
    { key: 'bankReference', label: 'Clave Rastreo', format: 'mono' },
    { key: 'transferStatus', label: 'Status', format: 'status' },
  ];
}

// ─── Format helpers ─────────────────────────────────────────────────────────

const fmtMXN = (v: unknown) =>
  typeof v === 'number'
    ? new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(v)
    : String(v ?? '-');

const fmtDate = (v: unknown) => {
  if (!v || typeof v !== 'string') return '-';
  try {
    return new Date(v).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: '2-digit' });
  } catch {
    return String(v);
  }
};

function formatCell(value: unknown, format?: string): string {
  if (format === 'currency') return fmtMXN(value);
  if (format === 'date') return fmtDate(value);
  return String(value ?? '-');
}

// ─── CSV export ─────────────────────────────────────────────────────────────

function exportCSV(items: Record<string, unknown>[], month: number, statusKey: string) {
  const cols = getDetailColumns(statusKey);
  const header = cols.map((c) => c.label).join(',');
  const rows = items.map((item) =>
    cols.map((c) => {
      const v = item[c.key];
      return typeof v === 'string' && v.includes(',') ? `"${v}"` : String(v ?? '');
    }).join(',')
  );
  const csv = [header, ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `conciliacion_pcf_${MONTH_NAMES[month - 1]}_${statusKey}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Component ──────────────────────────────────────────────────────────────

export function PCFDetailPanel({ selection, items, onClose }: Props) {
  const [page, setPage] = useState(1);
  const [modalItem, setModalItem] = useState<Record<string, unknown> | null>(null);

  const columns = getDetailColumns(selection.statusKey);
  const colConfig = PCF_SUMMARY_COLUMNS.find((c) => c.key === selection.statusKey);
  const statusLabel = colConfig?.label ?? selection.statusKey;
  const headerColors = HEADER_COLOR_MAP[colConfig?.color ?? 'green'] ?? HEADER_COLOR_MAP['green']!;
  const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));

  const pageItems = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return items.slice(start, start + PAGE_SIZE);
  }, [items, page]);

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className={`flex items-center justify-between rounded-t-lg border-b p-3 ${headerColors.border} ${headerColors.bg}`}>
        <div>
          <h3 className={`text-sm font-semibold ${headerColors.text}`}>
            {MONTH_NAMES[selection.month - 1]} — {statusLabel}
          </h3>
          <p className="text-xs text-muted-foreground">{items.length} registro(s)</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => exportCSV(items, selection.month, selection.statusKey)}
            disabled={items.length === 0}
          >
            <Download size={14} /> CSV
          </Button>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X size={14} />
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="mt-3 flex-1 overflow-auto rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/50">
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={`px-3 py-2 text-xs font-medium text-muted-foreground ${
                    col.format === 'currency' ? 'text-right' : 'text-left'
                  }`}
                >
                  {col.label}
                </th>
              ))}
              <th className="w-10 px-2 py-2" />
            </tr>
          </thead>
          <tbody>
            {pageItems.length === 0 ? (
              <tr>
                <td colSpan={columns.length + 1} className="px-3 py-8 text-center text-sm text-muted-foreground">
                  Sin registros
                </td>
              </tr>
            ) : (
              pageItems.map((item, i) => (
                <tr key={`row-${i.toString()}`} className="border-b border-border/50 hover:bg-muted/30">
                  {columns.map((col) => {
                    const val = item[col.key];

                    if (col.format === 'status') {
                      const statusStr = String(val ?? '-');
                      const badgeColor = STATUS_BADGE_COLORS[statusStr] ?? 'bg-slate-500/20 text-slate-400';
                      return (
                        <td key={col.key} className="px-2 py-2">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] ${badgeColor}`}>
                            {statusStr}
                          </span>
                        </td>
                      );
                    }

                    if (col.format === 'currency') {
                      const num = typeof val === 'number' ? val : undefined;
                      const isNegative = num !== undefined && num < 0;
                      return (
                        <td key={col.key} className={`px-3 py-2 text-right text-xs ${isNegative ? 'text-red-400' : 'text-green-400'}`}>
                          {formatCell(val, col.format)}
                        </td>
                      );
                    }

                    if (col.format === 'mono') {
                      return (
                        <td key={col.key} className="px-3 py-2 font-mono text-[10px]">
                          {String(val ?? '-')}
                        </td>
                      );
                    }

                    return (
                      <td key={col.key} className="px-3 py-2 text-xs">
                        {formatCell(val, col.format)}
                      </td>
                    );
                  })}
                  <td className="px-2 py-2">
                    <button
                      type="button"
                      onClick={() => setModalItem(item)}
                      className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                      aria-label="Ver detalle"
                    >
                      <Eye size={14} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-3 flex items-center justify-between">
          <span className="text-xs text-muted-foreground">
            Pagina {page} de {totalPages}
          </span>
          <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
        </div>
      )}

      {/* Detail modal (reuses CollectorDetailModal with PCF config) */}
      {modalItem && (
        <CollectorDetailModal
          item={modalItem}
          statusKey={selection.statusKey}
          onClose={() => setModalItem(null)}
          config={PCF_RECONCILIATION_CONFIG}
          summaryColumns={PCF_SUMMARY_COLUMNS}
        />
      )}
    </div>
  );
}
